import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FleetPage } from '../../sso-workspace/fleet-page'
import { NoAccess } from '../../../role/components/no-access'
import { AuditTimeline } from '../audit-timeline'
import { actorFilterKey, actorView, categoryLabel } from '../entry-format'
import { applyPivot, filtersToParams, filtersToSearch, insightParts, NO_INSIGHT_NARROWING, RANGES, withoutNetwork } from '../filters-model'
import { ActiveFilters, withinPart } from '../active-filters'
import { DayChip, useDayRange, useResultsInView } from '../day-range'
import { useAuditCapabilities } from '../use-audit-capabilities'
import { useFiltersInURL } from '../use-filters-url'
import { CalendarStrip } from '../insights/calendar-strip'
import { SummaryStrip } from './summary-strip'
import { SessionElsewhere, useSessionLabel } from '../session-label'
import '../audit.scss'
import '../audit-log/audit-log.scss'
import './activity.scss'
import { formatCount } from '../../sso-workspace/format'
import { SEARCH_FIELD_PROPS } from '../../component/search-field-props'

// Insights (Task 11) is its own chunk, ECharts and all.
const InsightsView = React.lazy(() => import('../insights/insights-view'))
// A pivot from Insights shows the timeline: it drops ?view and the
// Insights grid's own URL keys in the same history entry.
const LEAVE_INSIGHTS = k => k === 'view' || k.startsWith('launches.')

const SEARCH_DEBOUNCE_MS = 300
// Views and course launches are on by default here (mockup "User activity").
const DEFAULTS = { includeViews: true }

const LENSES = [
  { value: 'did', label: 'Did' },
  { value: 'done', label: 'Done to this account' },
  { value: 'both', label: 'Both' }
]
const DEFAULT_LENS = 'did'

const COPY = {
  person: {
    purpose: 'What this person did, and what was done to their account.',
    search: "Search this person's activity"
  },
  group: {
    purpose: "What the group's current members did. The group's own changes are under History.",
    search: "Search the group's activity"
  }
}

const WHEN = RANGES.filter(r => r.value !== 'custom')
const shortID = v => String(v || '').slice(0, 8)

// What the pivots narrowed the feed to, as removable chips (owner
// 2026-10-09; active-filters.js): [{ key, text, code?, node?, icon?,
// remove }], each remove the patch that takes it off. This page has no
// filter column, so every narrowing an entry offers is a chip here, all of
// them when several are stacked, and "Show everything" takes them all off.
// names: actor filter key -> name, from the loaded entries. session: the
// session filter in words ({ label, staff }; session-label.js), never its
// id -- staff see the id on hover. place: the within filter's node.
const without = (list, v) => list.filter(x => x !== v)
const narrowedBy = (f, names, session = {}, place = null) => {
  const parts = []
  if (f.actors.length === 1) {
    const k = f.actors[0]
    const name = k.startsWith('auto:') ? automaticJob(k) : names[k] || 'one person'
    parts.push({ key: 'actor', text: `Only ${name}`, remove: { actors: [] } })
  } else if (f.actors.length > 1) {
    parts.push({ key: 'actor', text: `${formatCount(f.actors.length)} people`, remove: { actors: [] } })
  }
  const w = withinPart(f.within, place)
  if (w) parts.push(w)
  f.apps.forEach(a => parts.push({ key: `app:${a}`, text: `Only ${a}`, remove: { apps: without(f.apps, a) } }))
  // what an Insights click can set that this page has no control for
  f.categories.forEach(c => parts.push({ key: `cat:${c}`, text: `Only ${categoryLabel(c)}`, remove: { categories: without(f.categories, c) } }))
  f.topics.forEach(t => parts.push({ key: `topic:${t}`, text: `Only ${t}`, remove: { topics: without(f.topics, t) } }))
  f.recordTypes.forEach(t => parts.push({ key: `type:${t}`, text: 'Record type', code: t, remove: { recordTypes: without(f.recordTypes, t) } }))
  f.actions.forEach(a => parts.push({ key: `action:${a}`, text: 'Action', code: a, remove: { actions: without(f.actions, a) } }))
  if (f.request) parts.push({ key: 'request', text: 'Request', code: shortID(f.request), title: f.request, remove: { request: '' } })
  if (f.session) parts.push({ key: 'session', text: session.label, title: session.staff ? f.session : undefined, remove: { session: '' } })
  if (f.ip) parts.push({ key: 'ip', text: 'IP', code: f.ip, remove: { ip: '' } })
  return parts
}

// auto:job:email-retention -> "the automatic job email retention"
const automaticJob = key => {
  const plain = key.slice(5).replace(/^(job|integration|migration|sso):/, '').replace(/[-_]/g, ' ').trim()
  return plain ? `the automatic job ${plain}` : 'an automatic job'
}

// Insights' narrowings (filters-model insightParts) as chips.
const INSIGHT_REMOVE = { verb: { verbs: [] }, outcome: { outcomes: [] }, records: { recordFilters: [] } }
const insightChips = f => insightParts(f).map(p => ({ ...p, text: p.text[0].toUpperCase() + p.text.slice(1), remove: INSIGHT_REMOVE[p.key] }))

// The Activity tab on a user or a group (spec §5; mockup "User activity"):
// the summary strip, then the timeline in the person's scope (person + lens,
// default "Did", owner question 7) or the group's (its current members).
// Search, the two toggles, the time range, the lens and any pivot all live
// in the URL, so a link reproduces the view; lens and view=insights are the
// page's own keys. Timeline | Insights (Task 11): Insights reads the feed's
// own params; the calendar strip sits above the timeline.
// Without an audit-log capability it is "No access", and nothing is read.
export const ActivityPage = ({ kind, id }) => {
  const caps = useAuditCapabilities()
  if (!caps.loaded) return null
  if (!caps.canAudit) return <NoAccess what="this activity" />
  return <Activity kind={kind} id={id} caps={caps} />
}

const Activity = ({ kind, id, caps }) => {
  const person = kind === 'person'
  const copy = person ? COPY.person : COPY.group
  const history = useHistory()
  const location = useLocation()
  const [filters, setURLFilters] = useFiltersInURL(DEFAULTS)
  const { range, back, pickDay, clearDay, setFilters } = useDayRange(filters, setURLFilters)

  const urlView = new URLSearchParams(location.search).get('view')
  const view = urlView === 'insights' ? 'insights' : 'timeline'
  const setView = next => {
    if (next === view) return
    const s = new URLSearchParams(location.search)
    if (next === 'insights') s.set('view', 'insights')
    else s.delete('view')
    const out = s.toString()
    history.replace({ pathname: location.pathname, search: out ? `?${out}` : '', state: location.state })
  }

  const urlLens = new URLSearchParams(location.search).get('lens')
  const lens = person && LENSES.some(l => l.value === urlLens) ? urlLens : DEFAULT_LENS
  const setLens = next => {
    const s = new URLSearchParams(location.search)
    if (next === DEFAULT_LENS) s.delete('lens')
    else s.set('lens', next)
    const out = s.toString()
    history.replace({ pathname: location.pathname, search: out ? `?${out}` : '', state: location.state })
  }

  const scopeID = String(id)
  // The summary always counts what the person did; the lens is the feed's.
  const base = useMemo(() => (person ? { person: scopeID, lens: 'did' } : { group: scopeID }), [person, scopeID])

  const filtersKey = filtersToSearch(filters, DEFAULTS)
  const params = useMemo(
    () => ({ ...(person ? { person: scopeID, lens } : { group: scopeID }), ...filtersToParams(withoutNetwork(filters, caps)) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [person, scopeID, lens, filtersKey]
  )

  // The search box: typing waits 300 ms before it becomes the q filter.
  const latest = useRef({ filters, setFilters })
  latest.current = { filters, setFilters }
  const [text, setText] = useState(filters.q)
  useEffect(() => setText(filters.q), [filters.q])
  useEffect(() => {
    if (text === latest.current.filters.q) return undefined
    const t = setTimeout(() => latest.current.setFilters({ ...latest.current.filters, q: text }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text])

  // Names for the actor pivot, from the entries loaded so far.
  const [names, setNames] = useState({})
  // Courses and folders by "nodes:<id>", for the within filter's chip: the
  // one the reader clicked, the one content's first page names, and every
  // place the loaded entries name.
  const [places, setPlaces] = useState({})
  const onItems = useCallback((items, record, within) => {
    setNames(prev => {
      let next = prev
      items.forEach(item => {
        const k = actorFilterKey(item.actor)
        if (k && !next[k]) next = { ...next, [k]: actorView(item.actor).text }
      })
      return next
    })
    setPlaces(prev => {
      let next = prev
      const add = p => {
        if (p && p.id && p.name && !next[`nodes:${p.id}`]) next = { ...next, [`nodes:${p.id}`]: p }
      }
      add(within)
      items.forEach(item => item.context && [item.context.course, item.context.folder].forEach(add))
      return next
    })
  }, [])

  const onPivot = ({ withinName, ...patch }) => {
    if (patch.within && withinName) setPlaces(prev => ({ ...prev, [patch.within]: { ...(prev[patch.within] || {}), name: withinName } }))
    setFilters({ ...filters, ...patch }, { push: true })
  }
  // Insights and the calendar strip narrow the timeline and show it.
  const pivotFromInsights = patch => setFilters(applyPivot(filters, patch), { push: true, drop: LEAVE_INSIGHTS })
  // The session the feed is narrowed to, in words, and its entries in other
  // organizations (a count; staff also get a button to them).
  const staff = Boolean(caps.sysAuditLog)
  const session = useSessionLabel(filters.session, params, { others: true })
  const narrowed = [...narrowedBy(filters, names, { label: session.label, staff }, places[filters.within]), ...insightChips(filters)]
  const removeOne = part => setFilters({ ...filters, ...part.remove }, { push: true })
  const showEverything = () => setFilters({ ...filters, request: '', session: '', ip: '', within: '', apps: [], actors: [], categories: [], topics: [], recordTypes: [], actions: [], ...NO_INSIGHT_NARROWING }, { push: true })

  const when = WHEN.some(r => r.value === filters.range) ? WHEN : [...WHEN, { value: filters.range, label: filters.range === 'custom' ? range.label : (RANGES.find(r => r.value === filters.range) || { label: 'Any time' }).label }]
  const results = useRef(null)
  useResultsInView(results, `${filtersKey}|${view}|${lens}`)

  return (
    <FleetPage title="Activity" purpose={copy.purpose}>
      <SummaryStrip base={base} />
      <div className="au-feedcard" ref={results}>
        <DayChip range={range} back={back} onClear={clearDay} />
        <div className="au-feedbar">
          <div className="au-seg" role="group" aria-label="View">
            <button type="button" className="btn sw-btn btn-sm" aria-pressed={view === 'timeline'} onClick={() => setView('timeline')}>Timeline</button>
            <button type="button" className="btn sw-btn btn-sm" aria-pressed={view === 'insights'} onClick={() => setView('insights')}>Insights</button>
          </div>
          {person &&
            <div className="au-seg" role="group" aria-label="Show">
              {LENSES.map(l =>
                <button key={l.value} type="button" className="btn sw-btn btn-sm" aria-pressed={lens === l.value} onClick={() => setLens(l.value)}>{l.label}</button>)}
            </div>}
          <input
            {...SEARCH_FIELD_PROPS}
            name="activity-search"
            className="form-control form-control-sm au-search"
            placeholder={copy.search}
            aria-label={copy.search}
            value={text}
            maxLength={200}
            onChange={e => setText(e.target.value)}
          />
          <select className="form-select form-select-sm au-when" aria-label="When" value={filters.range} onChange={e => setFilters({ ...filters, range: e.target.value })}>
            {when.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="au-act-toggles">
          <label className="au-check"><input type="checkbox" checked={filters.includeViews} onChange={e => setFilters({ ...filters, includeViews: e.target.checked })} />Include views and course launches</label>
          <label className="au-check"><input type="checkbox" checked={filters.includeAutomatic} onChange={e => setFilters({ ...filters, includeAutomatic: e.target.checked })} />Include automatic changes</label>
        </div>

        <ActiveFilters parts={narrowed} onRemove={removeOne} onClear={showEverything} />
        {filters.session && <SessionElsewhere sessionID={filters.session} summary={session.summary} error={session.error} staff={staff} filters={filters} />}

        {view === 'insights'
          ? <Suspense fallback={<p className="sw-hint">Loading charts…</p>}>
              <InsightsView params={params} onPivot={pivotFromInsights} onPickDay={pickDay} range={range} context="activity" />
            </Suspense>
          : <>
              <CalendarStrip params={params} range={range} onPickDay={pickDay} />
              <AuditTimeline params={params} context="activity" caps={caps} onPivot={onPivot} onItems={onItems} emptyText="No activity in this time range." />
            </>}
      </div>
    </FleetPage>
  )
}
